import React, { useState, useEffect } from "react";
import { Search, SlidersHorizontal, User, Calendar, Package, UserCircle, Mail, Phone, MapPin, CreditCard, ClipboardList, X } from "lucide-react";
import "./viewBookedOrder.css";
import { useSelector, useDispatch } from "react-redux";
import { BsArrowBarLeft } from "react-icons/bs";
import { uiAlert } from "../../../../components/alerts/customAlert/uiAlert";
import {
  cancelBookedQuotationUrl,
  convertQuotationToSalesUrl,
  createTransactionUrl,
  deleteTransactionUrl,
  fetchBookedQuotationUrl,
  updateBookedQuotationUrl,
} from "../../../../api/endpoints";
import { ButtonLoaderII } from "../../../../components/uxCollections/Loaders";
import { generateTransactionID } from "../operationTypeCustomerDetails/OperationType";
import { setUser } from "../../../../slice/webStoreUserSlice";
import { modifyTansactionData } from "../../../../tools/indexdb";
import { updateNotification } from "../../../../slice/notificationSlice";
import PaymentConfirmationModal from "./BookedOrderConfirmation";

const BookedQuotations = ({ whichCompToRender }) => {
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [currentFilter, setCurrentFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [showMobileModal, setShowMobileModal] = useState(false);
  const [showUpdatePriceModal, setShowUpdatePriceModal] = useState(false);
  const [bookedQuotations, setBookedQuotations] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 850);
  const [isItemSupplied, setIsItemSupplied] = useState(true);
  const [openConfirmOrderModal, setOpenConfirmOrderModal] = useState(false);
  const [paymentType, setPaymentType] = useState("full");
  const [initialPayment, setInitialPayment] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [balance, setBalance] = useState(0);
  const [isloading, setIsLoading] = useState(true);
  const [priceOrQuantityUpdated, setPriceOrQuantityUpdated] = useState(false);
  const { store } = useSelector((state) => state.store);
  const { user } = useSelector((state) => state.webStoreUser);
  const { employee } = useSelector((state) => state.employee);
  const dispatch = useDispatch();

  useEffect(() => {
    fetchBookerQuotations();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 850;
      setIsMobile(mobile);

      if (selectedOrder) {
        if (mobile) {
          setShowMobileModal(true);
        } else {
          setShowMobileModal(false);
        }
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [selectedOrder]);

  const fetchBookerQuotations = async () => {
    const url = fetchBookedQuotationUrl + store._id; // + storeID
    try {
      let response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
      });

      if (response.status === 404) {
        uiAlert("No booked quotations found for this store.", "info");
        setBookedQuotations([]);
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      let data = await response.json();
      console.log("Booked Quotations Data:", data);
      setBookedQuotations(data.bookedOrders);
    } catch (error) {
      console.error("Error fetching booked quotations:", error);
      uiAlert("Failed to load booked quotations. Please try again later.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatCurrency = (amount, currency = "₦") => {
    return `${currency}${amount.toLocaleString()}`;
  };

  const hasNegotiation = (products) => {
    return products.some((p) => p.requestedUnitPrice !== null || p.requestedQuantity !== null);
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { label: "Pending", class: "bq-badge-pending" },
      completed: { label: "Completed", class: "bq-badge-completed" },
      modified: { label: "Modified", class: "bq-badge-modified" },
      rejected: { label: "Rejected", class: "bq-badge-rejected" },
    };
    const { label, class: className } = config[status] || config.pending;
    return <span className={`bq-badge ${className}`}>{label}</span>;
  };

  const filteredOrders = bookedQuotations.filter((order) => {
    const matchesFilter = currentFilter === "all" || order.status === currentFilter;
    const matchesSearch =
      !searchTerm ||
      order.bookedOrderID.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.customer.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.customer.email.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const selectOrder = (order) => {
    console.log("Selected order:", order);

    setSelectedOrder(order);
    if (isMobile) {
      setShowMobileModal(true);
    }
  };

  function truncateHash(str, start = 5, end = 2) {
    if (!str || str.length <= start + end) return str;
    return `${str.slice(0, start)}...${str.slice(-end)}`;
  }

  const closeMobileModal = () => {
    setShowMobileModal(false);
    setSelectedOrder(null);
  };

  const handleUpdatePrice = () => {
    setShowUpdatePriceModal(true);
  };

  const closeUpdatePriceModal = () => {
    setShowUpdatePriceModal(false);
  };

  const saveUpdatedPrices = async () => {
    //;
    try {
      // set the requested prices and quantities to null
      const updatedProducts = selectedOrder.products.map((product) => {
        return {
          ...product,
          requestedUnitPrice: null,
          requestedQuantity: null,
        };
      });

      const subTotal = updatedProducts.reduce((sum, product) => sum + Number(product.soldUnitPrice) * product.quantity, 0);
      const vat = (subTotal * selectedOrder.bookedOrderDetails.vatPercentage.$numberDecimal) / 100;
      const totalAmount = subTotal - selectedOrder.bookedOrderDetails.discount + vat;
      const bookedOrderDetails = {
        ...selectedOrder.bookedOrderDetails,
        subTotal,
        vat,
        totalAmount,
        modified: true,
      };
      console.log("Updating booked quotation with:", { products: updatedProducts, bookedOrderDetails });

      let response = await fetch(updateBookedQuotationUrl + store._id + "/" + selectedOrder._id, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
        body: JSON.stringify({
          products: updatedProducts,
          bookedOrderDetails,
        }),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      let { updatedBookedOrder } = await response.json();
      await fetchBookerQuotations(); // refresh the list
      setSelectedOrder(updatedBookedOrder);
      setPriceOrQuantityUpdated(false);
      uiAlert("Prices and quantities updated successfully. The quotation has been sent to the customer", "success");
    } catch (error) {
      console.error("Error updating prices:", error);
      uiAlert("Failed to update prices. Please try again later.", "error");
      return;
    } finally {
      closeUpdatePriceModal();
    }
  };

  const acceptNegotiation = async () => {
    //set the requested prices and quantities as the new sold prices and quantities

    if (!window.confirm("Are you sure you want to accept the customer's negotiation? This will update the quotation accordingly.")) {
      return;
    }

    try {
      const updatedProducts = selectedOrder.products.map((product) => {
        return {
          ...product,
          soldUnitPrice: product.requestedUnitPrice !== null ? product.requestedUnitPrice : product.soldUnitPrice,
          quantity: product.requestedQuantity !== null ? product.requestedQuantity : product.quantity,
          requestedUnitPrice: null,
          requestedQuantity: null,
        };
      });
      const payload = {
        products: updatedProducts,
        bookedOrderDetails: selectedOrder.bookedOrderDetails,
        acceptedNegotiation: true,
      };

      const url = updateBookedQuotationUrl + store._id + "/" + selectedOrder._id; // + storeID + bookedOrderID

      let response = await fetch(url, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      let { updatedBookedOrder } = await response.json();
      await fetchBookerQuotations(); // refresh the list
      setSelectedOrder(updatedBookedOrder);
      uiAlert("Customer negotiation accepted successfully.", "success");
    } catch (error) {
      console.error("Error accepting negotiation:", error);
      uiAlert("Failed to accept negotiation. Please try again later.", "error");
      return;
    } finally {
      if (isMobile) {
        closeMobileModal();
      }
    }
  };

  function stripLeadingZeros(value) {
    if (value == null) return 0; // handle null/undefined
    return Number(value.toString().replace(/^0+/, "") || "0");
  }

  const confirmOrder = async () => {
    if (window.confirm("Are you sure you want to confirm this order and add it to sales?")) {
      let transactionPayload = {
        transactionDetails: {
          date: new Date(),
          deliveryStatus: isItemSupplied ? "supplied" : "notSupplied",
          paymentMode: selectedOrder.bookedOrderDetails.paymentMode,
          numberOfItems: selectedOrder.bookedOrderDetails.numberOfItems,
          totalAmount: selectedOrder.bookedOrderDetails.totalAmount,
          discount: selectedOrder.bookedOrderDetails.discount,
          vat: selectedOrder.bookedOrderDetails.vat,
          subTotal: selectedOrder.bookedOrderDetails.subTotal,
          vatPercentage: selectedOrder.bookedOrderDetails.vatPercentage,
        },
        soldBy: {
          employeeID: employee ? employee._id : null,
          fullName: employee ? employee.employeeName.split(" ")[0] : "Unknown Employee",
        },
        customer: {
          customerID: selectedOrder.customer?.customerID || null,
          fullName: selectedOrder.customer?.fullName,
          email: selectedOrder.customer?.email,
          phoneNumber: selectedOrder.customer?.phoneNumber,
          address: selectedOrder.customer?.address,
          storePurchaseFrom: {
            storeName: selectedOrder.store.name,
          },
          purchaseChannel: "inStore",
          totalPurchaseAmount: selectedOrder.bookedOrderDetails.totalAmount,
          userType: "notOnPlatform",
          amountOwned: 0,
        },
        webstoreUserID: selectedOrder.webstoreUserID || null,
        store: selectedOrder.store,
        products: selectedOrder.products.map((product) => ({
          id: product.id,
          name: product.name,
          category: product.category,
          productCostPrice: product.productCostPrice,
          soldUnitPrice: product.soldUnitPrice,
          serialNumber: product.serialNumber || null,
          isSubUnitSold: product.isSubUnitSold || false,
          subUnitId: product.subUnitId || null,
          unit: product.unit || null,
          quantity: product.quantity,
          totalAmount: product.totalAmount,
          productImage: product.productImage || "	https://res.cloudinary.com/ebuka1122/image/upload/…6/static%20images%20storelens/download_bvjych.png",
          returned: false,
          returnedDate: null,
          supplied: isItemSupplied,
          suppliedDate: isItemSupplied ? new Date() : null,
        })),
        operationType: {
          whichOperation: paymentType === "full" ? "cash" : "loan",
          loanDate: paymentType !== "full" ? new Date() : null,
          intialPayment: paymentType !== "full" ? stripLeadingZeros(initialPayment) : null,

          balance: paymentType !== "full" ? Math.ceil(selectedOrder.bookedOrderDetails.totalAmount - stripLeadingZeros(initialPayment)) : null,
          dateToPay: paymentType !== "full" ? dueDate : null,
          loanDuration: paymentType !== "full" ? Math.ceil(new Date(dueDate) - new Date()) : null, // milliseconds
        },
        comments: selectedOrder.comments,
        transID: generateTransactionID(),
      };
      console.log("Confirming order with payload:", transactionPayload);

      //create a new transaction in the sales module here

      try {
        setIsLoading(true);
        let response = await fetch(createTransactionUrl + store._id, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
          body: JSON.stringify(transactionPayload),
        });
        if (!response.ok) {
          console.error("Error creating transaction:", response.statusText);
          uiAlert("Failed to create transaction. Please try again later.", "error");
          return;
        }
        let { user, shouldSendNotification, newTransaction } = await response.json();

        dispatch(setUser(user));

        //mark the quotation as completed
        let proceed = await markQuotationAsCompleted(newTransaction._id);
        if (proceed) {
          // update the sales record offline DB
          const [a, b, c, error] = await modifyTansactionData(store.storeName, newTransaction, "todayTransactions");

          if (error) {
            return uiAlert(error || "Failed to confirm and add quotation to sales. Check stock quantity", "error");
          }
          dispatch(updateNotification(shouldSendNotification));
        }
      } catch (error) {
        console.error("Error confirming order:", error);
        uiAlert("Failed to confirm and add quotation to sales. Please try again later.", "error");
        return;
      } finally {
        setIsLoading(false);
      }
    }
  };

  const markQuotationAsCompleted = async (transID) => {
    try {
      let url = convertQuotationToSalesUrl + store._id + "/" + selectedOrder._id; // + storeID + bookedOrderID

      let response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
      });
      if (response.status === 404) {
        uiAlert("This quotation can't be updated. It may have already been processed.", "info");
        throw new Error("Quotation already processed");
      }
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      let { bookedOrder } = await response.json();
      // Update the order status in state
      setBookedQuotations((prevOrders) =>
        prevOrders.map((order) => (order._id === bookedOrder._id ? { ...order, status: "completed", customerHasPaid: true } : order))
      );

      uiAlert("Order confirmed and added to sales successfully!", "success");
      if (isMobile) {
        closeMobileModal();
      }
      return true;
    } catch (error) {
      console.error("Error confirming order:", error);
      uiAlert("Failed to confirm and add quotation to sales. Rolling back.", "error");
      rollBackTransaction(transID);
      return false;
    }
  };

  const rollBackTransaction = async (transID) => {
    console.log("Rolling back transaction with ID:", transID);

    try {
      let response = await fetch(deleteTransactionUrl + store._id + "/" + transID, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
      });
    } catch (error) {
      console.error("Error rolling back transaction:", error);
      uiAlert("Failed to roll back transaction after quotation confirmation failure. Please contact support.", "error");
      return;
    }
  };

  const cancelOrder = async () => {
    if (window.confirm("Are you sure you want to cancel and delete this order?")) {
      try {
        setIsLoading(true);
        let url = cancelBookedQuotationUrl + store._id + "/" + selectedOrder._id; // + storeID + orderID

        let response = await fetch(url, {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        });

        if (response.status === 404) {
          uiAlert("Order not found. It may have already been deleted.", "info");
          return;
        }
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        let { deletedOrder } = await response.json();

        // Remove the deleted order from state
        setBookedQuotations((prevOrders) => prevOrders.filter((order) => order._id !== deletedOrder._id));
        setSelectedOrder(null);

        alert("Order cancelled and deleted!");
        if (isMobile) {
          closeMobileModal();
        }
      } catch (error) {
        console.error("Error cancelling order:", error);
        uiAlert("Failed to cancel the order. Please try again later.", "error");
        return;
      } finally {
        setIsLoading(false);
      }
    }
  };

  const renderDetailContent = (order) => (
    <>
      <div className='bq-section'>
        <div className='bq-section-header'>
          <span className='bq-section-icon'>
            <UserCircle size={18} />
          </span>
          <h3 className='bq-section-title'>Customer Information</h3>
        </div>
        <div className='bq-info-grid'>
          <div className='bq-info-item'>
            <User size={18} className='bq-info-icon' />
            <div className='bq-info-content'>
              <span className='bq-info-value'>{order.customer.fullName}</span>
            </div>
          </div>
          <div className='bq-info-item'>
            <Mail size={18} className='bq-info-icon' />
            <div className='bq-info-content'>
              <span className='bq-info-value'>{order.customer.email}</span>
            </div>
          </div>
          <div className='bq-info-item'>
            <Phone size={18} className='bq-info-icon' />
            <div className='bq-info-content'>
              <span className='bq-info-value'>{order.customer.phoneNumber}</span>
            </div>
          </div>
          <div className='bq-info-item'>
            <MapPin size={18} className='bq-info-icon' />
            <div className='bq-info-content'>
              <span className='bq-info-value'>{order.customer.address}</span>
            </div>
          </div>
        </div>
      </div>

      <div className='bq-section'>
        <div className='bq-section-header'>
          <span className='bq-section-icon'>
            <CreditCard size={18} />
          </span>
          <h3 className='bq-section-title'>Payment Information</h3>
        </div>
        <div className='bq-info-grid'>
          <div className='bq-info-item'>
            <div className='bq-info-content'>
              <span className='bq-info-label'>Bank:</span>
              <span className='bq-info-value'>{order.paymentDetails.bankName}</span>
            </div>
          </div>
          <div className='bq-info-item'>
            <div className='bq-info-content'>
              <span className='bq-info-label'>Account:</span>
              <span className='bq-info-value'>{order.paymentDetails.accountNumber}</span>
            </div>
          </div>
          <div className='bq-info-item'>
            <div className='bq-info-content'>
              <span className='bq-info-label'>Payment Mode:</span>
              <span className='bq-info-value'>{order.bookedOrderDetails.paymentMode}</span>
            </div>
          </div>
          <div className='bq-info-item'>
            <div className='bq-info-content'>
              <span className='bq-info-label'>Payment Status:</span>
              <span className='bq-info-value-highlight'>{order.customerHasPaid ? "Paid" : "Not Paid"}</span>
            </div>
          </div>
        </div>
      </div>

      <div className='bq-section'>
        <div className='bq-section-header'>
          <span className='bq-section-icon'>
            <ClipboardList size={18} />
          </span>
          <h3 className='bq-section-title'>Order Details</h3>
        </div>
        <div className='bq-order-summary-grid'>
          <div className='bq-summary-item'>
            <span className='bq-summary-label'>Date:</span>
            <span className='bq-summary-value'>{formatDate(order.bookedOrderDetails.date)}</span>
          </div>
          <div className='bq-summary-item'>
            <span className='bq-summary-label'>Booked By:</span>
            <span className='bq-summary-value'>{order.bookedBy}</span>
          </div>
          <div className='bq-summary-item'>
            <span className='bq-summary-label'>Sent Via:</span>
            <span className='bq-summary-value'>{order.sentVia}</span>
          </div>
          <div className='bq-summary-item'>
            <span className='bq-summary-label'>Items:</span>
            <span className='bq-summary-value'>{order.bookedOrderDetails.numberOfItems}</span>
          </div>
        </div>
      </div>

      <div className='bq-section'>
        <div className='bq-products-header'>
          <div className='bq-section-header' style={{ marginBottom: 0, paddingBottom: 0, borderBottom: "none" }}>
            <span className='bq-section-icon'>
              <Package size={18} />
            </span>
            <h3 className='bq-section-title'>Products</h3>
          </div>
          <span className='bq-products-count'>{order.products.length}</span>
        </div>
        <div className='bq-products-list'>
          {order.products.map((product, i) => (
            <div key={i} className={`bq-product-item ${!product.requestedUnitPrice && !product.requestedQuantity ? "no-negotiation" : ""}`}>
              <img src={product.productImage} alt={product.name} className='bq-product-image' />
              <div className='bq-product-details'>
                <div className='bq-product-header'>
                  <div>
                    <div className='bq-product-name'>{product.name}</div>
                    <div className='bq-product-category'>{product.category}</div>
                  </div>
                  {(product.requestedUnitPrice || product.requestedQuantity) && <span className='bq-negotiation-badge-small'>⚠️ Negotiated</span>}
                </div>

                <div className='bq-product-pricing'>
                  <div className='bq-price-item'>
                    <span className='bq-price-label'>Unit Price:</span>
                    <span className={`bq-price-value ${product.requestedUnitPrice ? "bq-price-strikethrough" : ""}`}>
                      {formatCurrency(product.soldUnitPrice, order.currency)}
                    </span>
                    {product.requestedUnitPrice && (
                      <span className='bq-price-value bq-price-negotiated'>
                        {formatCurrency(product.requestedUnitPrice, order.currency)}
                        <span className='bq-price-diff'>(-{formatCurrency(product.soldUnitPrice - product.requestedUnitPrice, order.currency)})</span>
                      </span>
                    )}
                  </div>
                  <div className='bq-price-item'>
                    <span className='bq-price-label'>Quantity ({product.unit}) :</span>
                    <span className={`bq-price-value ${product.requestedQuantity ? "bq-price-strikethrough" : ""}`}>{product.quantity}</span>
                    {product.requestedQuantity && (
                      <span className='bq-price-value bq-price-negotiated'>
                        {product.requestedQuantity}
                        <span className='bq-price-diff bq-qty-change'>(+{product.requestedQuantity - product.quantity})</span>
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className='bq-total-summary'>
          <div className='bq-summary-row'>
            <span>Subtotal</span>
            <span>{formatCurrency(order.bookedOrderDetails.subTotal, order.currency)}</span>
          </div>
          {order.bookedOrderDetails.discount > 0 && (
            <div className='bq-summary-row'>
              <span>Discount</span>
              <span className='bq-discount-value'>-{formatCurrency(order.bookedOrderDetails.discount, order.currency)}</span>
            </div>
          )}
          <div className='bq-summary-row'>
            <span>VAT</span>
            <span>{formatCurrency(order.bookedOrderDetails.vat, order.currency)}</span>
          </div>
          <div className='bq-summary-row total'>
            <span>Total Amount</span>
            <span>{formatCurrency(order.bookedOrderDetails.totalAmount, order.currency)}</span>
          </div>
        </div>
      </div>
    </>
  );

  const renderFooterButtons = (order) => (
    <>
      {hasNegotiation(order.products) ? (
        <>
          <button className='bq-btn bq-btn-secondary' onClick={handleUpdatePrice}>
            Update Price
          </button>
          <button className='bq-btn bq-btn-primary' onClick={acceptNegotiation}>
            Accept Customer's Price
          </button>
        </>
      ) : (
        <>
          <button className='bq-btn bq-btn-danger' onClick={cancelOrder}>
            Cancel & Delete
          </button>
          <button
            style={{
              backgroundColor: isloading || selectedOrder.customerHasPaid ? "rgba(0, 128, 0, 0.5)" : " #28a745",
            }}
            disabled={isloading || selectedOrder.customerHasPaid}
            className='bq-btn bq-btn-success'
            onClick={() => setOpenConfirmOrderModal(true)}
          >
            Confirm & Add to Sales
          </button>
        </>
      )}
    </>
  );

  return (
    <div className='bq-main-layout'>
      {/* Left Panel - Orders List */}
      <div className='bq-left-panel'>
        <div className='bq-header'>
          <div style={{ display: "flex", alignItems: "center", gap: "1rem", marginBottom: "0.5rem" }}>
            <span onClick={() => whichCompToRender("CashTransactionComp")} className='bq-back-button'>
              <BsArrowBarLeft size={20} className='bq-back-icon' />
            </span>
            <h1 className='bq-title'>Booked Quotations</h1>
          </div>
          <p className='bq-subtitle'>Manage customer quotations and negotiations</p>
        </div>

        <div className='bq-controls'>
          <div className='bq-search'>
            <Search size={18} className='bq-search-icon' />
            <input type='text' placeholder='Search by ID, customer name, or email...' value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
          </div>
          <div className='bq-filter-wrapper'>
            <SlidersHorizontal size={20} className='bq-filter-icon' />
            <div className='bq-filter-tabs'>
              {["all", "pending", "modified"].map((filter) => (
                <button key={filter} className={`bq-tab ${currentFilter === filter ? "active" : ""}`} onClick={() => setCurrentFilter(filter)}>
                  {filter.charAt(0).toUpperCase() + filter.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className='bq-orders-container'>
          <div className='bq-orders-list'>
            {isloading ? (
              <div
                style={{
                  height: "100%",
                  minHeight: "200px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <ButtonLoaderII size={30} color={"#0E49B5"} />
              </div>
            ) : filteredOrders.length === 0 ? (
              <div className='bq-empty'>
                <div className='bq-empty-icon'>📋</div>
                <p>No quotations found</p>
              </div>
            ) : (
              filteredOrders.map((order) => (
                <div
                  key={order._id}
                  className={`bq-order-card ${selectedOrder && selectedOrder._id === order._id ? "selected" : ""}`}
                  onClick={() => {
                    console.log("Order clicked:", order._id);
                    selectOrder(order);
                  }}
                >
                  <div className='bq-order-header'>
                    <div className='bq-order-id-wrapper'>
                      <div className='bq-order-id'>
                        {/* {order.bookedOrderID} */}
                        {hasNegotiation(order.products) && <span className='bq-badge bq-badge-negotiation'>⚠️ Negotiation</span>}
                      </div>
                      <div className='bq-store-name'>{order.store.name}</div>
                    </div>
                    <div className='bq-badges'>{getStatusBadge(order.status)}</div>
                  </div>

                  <div className='bq-order-meta'>
                    <div className='bq-meta-item'>
                      <User size={16} className='bq-meta-icon' />
                      <span className='bq-meta-value'>{order.customer.fullName}</span>
                    </div>
                    <div className='bq-meta-item'>
                      <Calendar size={16} className='bq-meta-icon' />
                      <span className='bq-meta-value'>{formatDate(order.bookedOrderDetails.date)}</span>
                    </div>
                    <div className='bq-meta-item'>
                      <Package size={16} className='bq-meta-icon' />
                      <span className='bq-meta-value'>{order.bookedOrderDetails.numberOfItems} items</span>
                    </div>
                    <div className='bq-meta-item'>
                      <span className={`bq-payment-status ${order.customerHasPaid ? "bq-payment-paid" : "bq-payment-unpaid"}`}>
                        {order.customerHasPaid ? "Paid" : "Unpaid"}
                      </span>
                    </div>
                  </div>

                  <div className='bq-order-footer'>
                    <div className='bq-total-section'>
                      <span className='bq-total-label'>Total</span>
                      <span className='bq-total'>{formatCurrency(order.bookedOrderDetails.totalAmount, order.currency)}</span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
      {/* Right Panel - Order Details (Desktop) */}
      <div className={`bq-right-panel ${selectedOrder && !isMobile ? "active" : ""}`}>
        {selectedOrder ? (
          <>
            <div className='bq-detail-header'>
              <div className='bq-detail-title-section'>
                <div className='bq-detail-id'>
                  {truncateHash(selectedOrder.bookedOrderID)}
                  {getStatusBadge(selectedOrder.status)}
                </div>
                <div className='bq-detail-store'>{selectedOrder.store.name}</div>
              </div>
            </div>
            <div className='bq-detail-content'>{renderDetailContent(selectedOrder)}</div>
            <div className='bq-detail-footer'>{renderFooterButtons(selectedOrder)}</div>
          </>
        ) : (
          <div className='bq-detail-content'>
            <div className='bq-empty-detail'>
              <img
                style={{
                  width: "200px",

                  marginBottom: "1rem",
                }}
                src='https://res.cloudinary.com/ebuka1122/image/upload/v1768209345/static%20images%20storelens/no-quotation-empty-state-400px-hq_vqdo5m.webp'
                alt='No Selection'
                className='bq-empty-detail-icon'
              />

              <h3
                style={{
                  marginBottom: "0.5rem",
                }}
              >
                No Quotation Selected
              </h3>
              <p>Select a quotation to view details</p>
            </div>
          </div>
        )}
      </div>
      {/* Mobile Overlay Modal */}
      {showMobileModal && selectedOrder && (
        <div className='bq-modal-overlay active' onClick={closeMobileModal}>
          <div className='bq-modal' onClick={(e) => e.stopPropagation()}>
            <div className='bq-detail-header'>
              <div className='bq-detail-title-section'>
                <div className='bq-detail-id'>
                  {truncateHash(selectedOrder.bookedOrderID)}
                  {getStatusBadge(selectedOrder.status)}
                </div>
                <div className='bq-detail-store'>{selectedOrder.store.name}</div>
              </div>
              <button className='bq-close-btn' onClick={closeMobileModal}>
                <X size={20} />
              </button>
            </div>
            <div className='bq-detail-content'>{renderDetailContent(selectedOrder)}</div>
            <div className='bq-detail-footer'>{renderFooterButtons(selectedOrder)}</div>
          </div>
        </div>
      )}
      {/* Update Price Modal */}
      {showUpdatePriceModal && selectedOrder && (
        <div className='bq-update-modal active' onClick={closeUpdatePriceModal}>
          <div className='bq-update-modal-content' onClick={(e) => e.stopPropagation()}>
            <div className='bq-update-modal-header'>
              <h2 className='bq-update-modal-title'>Update Prices</h2>
              <button className='bq-close-btn' onClick={closeUpdatePriceModal}>
                <X size={20} />
              </button>
            </div>
            <div className='bq-update-modal-body'>
              {selectedOrder.products.map((product, i) => (
                <div key={i} className='bq-update-item'>
                  <div className='bq-update-product-header'>
                    <img src={product.productImage} alt={product.name} className='bq-update-product-img' />
                    <span className='bq-update-product-name'>{product.name}</span>
                  </div>
                  <div className='bq-update-inputs-grid'>
                    <div className='bq-input-group'>
                      <label className='bq-input-label'>Unit Price ({selectedOrder.currency})</label>
                      <input
                        type='number'
                        inputMode='decimal'
                        className='bq-input'
                        value={product.soldUnitPrice}
                        onChange={(e) => {
                          const newPrice = e.target.value;
                          setSelectedOrder((prevOrder) => ({
                            ...prevOrder,
                            products: prevOrder.products.map((p) => (p.id === product.id ? { ...p, soldUnitPrice: Number(newPrice) } : p)),
                          }));
                          setPriceOrQuantityUpdated(true);
                        }}
                      />
                    </div>
                    <div className='bq-input-group'>
                      <label className='bq-input-label'>Quantity ({product.unit}) </label>
                      <input
                        type='number'
                        inputMode='decimal'
                        className='bq-input'
                        value={product.quantity}
                        onChange={(e) => {
                          const newQty = e.target.value;
                          setSelectedOrder((prevOrder) => ({
                            ...prevOrder,
                            products: prevOrder.products.map((p) => (p.id === product.id ? { ...p, quantity: Number(newQty) } : p)),
                          }));
                          setPriceOrQuantityUpdated(true);
                        }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className='bq-update-modal-footer'>
              <button className='bq-btn bq-btn-secondary' onClick={closeUpdatePriceModal}>
                Cancel
              </button>
              <button disabled={!priceOrQuantityUpdated} className='bq-btn bq-btn-primary' onClick={saveUpdatedPrices}>
                Save & Send Update
              </button>
            </div>
          </div>
        </div>
      )}
      {openConfirmOrderModal && (
        <PaymentConfirmationModal
          isOpen={openConfirmOrderModal}
          onClose={() => {
            setOpenConfirmOrderModal(false);
          }}
          onConfirm={confirmOrder}
          orderTotal={selectedOrder.bookedOrderDetails.totalAmount}
          paymentType={paymentType}
          setPaymentType={setPaymentType}
          initialPayment={initialPayment}
          setInitialPayment={setInitialPayment}
          dueDate={dueDate}
          setDueDate={setDueDate}
          balance={balance}
          setBalance={setBalance}
          currency={user.userCountryCurrencySymbol || "₦"}
        />
      )}
    </div>
  );
};

export default BookedQuotations;
